import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { CACHE_TIME } from '@hygge/shared';
import { getEmployees, getEmployeeStats } from '@/lib/employees';
import type { EmployeeQueryParams, EmployeesResponse } from '@/types/employee';

/** Query key factory for employees */
export const employeesKeys = {
  all: ['employees'] as const,
  lists: () => [...employeesKeys.all, 'list'] as const,
  list: (params: EmployeeQueryParams) => [...employeesKeys.lists(), params] as const,
  infinite: (params: Omit<EmployeeQueryParams, 'page'>) => [...employeesKeys.lists(), 'infinite', params] as const,
  stats: () => [...employeesKeys.all, 'stats'] as const,
  details: () => [...employeesKeys.all, 'detail'] as const,
  detail: (id: string) => [...employeesKeys.details(), id] as const,
};

/**
 * Hook to fetch paginated employees list
 */
export function useEmployees(params: EmployeeQueryParams = {}) {
  return useQuery({
    queryKey: employeesKeys.list(params),
    queryFn: () => getEmployees(params),
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Hook to fetch employees with infinite scroll
 */
export function useInfiniteEmployees(params: Omit<EmployeeQueryParams, 'page'> = {}) {
  return useInfiniteQuery<EmployeesResponse>({
    queryKey: employeesKeys.infinite(params),
    queryFn: ({ pageParam = 1 }) => getEmployees({ ...params, page: pageParam as number }),
    initialPageParam: 1,
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage.meta;
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: CACHE_TIME.DEFAULT,
  });
}

/**
 * Hook to fetch employee statistics
 */
export function useEmployeeStats(params?: Omit<EmployeeQueryParams, 'page' | 'limit'>) {
  return useQuery({
    queryKey: [...employeesKeys.stats(), params],
    queryFn: () => getEmployeeStats(params),
    staleTime: CACHE_TIME.DEFAULT,
  });
}

